import { useCallback, useEffect, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, List, ListItem, Tooltip, Typography, alpha, useTheme,
} from '@mui/material'
import { Close, DeleteOutline, Download, GraphicEq, PlayArrow, Refresh } from '@mui/icons-material'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import { ConfirmDialog } from '../dialogKit'

const fmtDuration = (s) => {
  const h = Math.floor(s / 3600)
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
  const sec = String(s % 60).padStart(2, '0')
  return h > 0 ? `${h}:${m}:${sec}` : `${m}:${sec}`
}

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`)

const RecordingsDialog = ({ open, onClose, groupId, groupName, iAmOwner, myId }) => {
  const theme = useTheme()
  const api = useAxiosPrivate()
  const isDark = theme.palette.mode === 'dark'
  const accent = theme.palette.secondary.main

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [playing, setPlaying] = useState(null)       // { id, url }
  const [busyId, setBusyId] = useState(null)
  const [confirmRec, setConfirmRec] = useState(null)  // recording waiting for delete confirmation

  const load = useCallback(async () => {
    if (!groupId) return
    setLoading(true)
    setError('')
    try {
      const { data } = await api.get('/api/recordings', { params: { groupId } })
      setItems(data)
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to load the recordings')
    } finally {
      setLoading(false)
    }
  }, [api, groupId])

  const stopPlayer = useCallback(() => {
    setPlaying((p) => {
      if (p) URL.revokeObjectURL(p.url)
      return null
    })
  }, [])

  useEffect(() => {
    if (open) load()
    else stopPlayer()
  }, [open, load, stopPlayer])

  // Recordings that are still being written: check again every few seconds
  useEffect(() => {
    if (!open || !items.some((i) => ['starting', 'recording', 'processing'].includes(i.status))) return
    const timer = setInterval(load, 8000)
    return () => clearInterval(timer)
  }, [open, items, load])

  const getBlobUrl = async (id) => {
    const res = await api.get(`/api/recordings/${id}/file`, { responseType: 'blob' })
    return URL.createObjectURL(res.data)
  }

  const handlePlay = async (rec) => {
    if (playing?.id === rec._id) return stopPlayer()

    setBusyId(rec._id)
    setError('')
    try {
      const url = await getBlobUrl(rec._id)
      stopPlayer()
      setPlaying({ id: rec._id, url })
    } catch (err) {
      setError('Unable to load this recording')
    } finally {
      setBusyId(null)
    }
  }

  const handleDownload = async (rec) => {
    setBusyId(rec._id)
    setError('')
    try {
      const url = await getBlobUrl(rec._id)
      const stamp = new Date(rec.startedAt).toISOString().slice(0, 16).replace(/[:T]/g, '-')
      const a = document.createElement('a')
      a.href = url
      a.download = `meeting-${stamp}.ogg`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError('Unable to download this recording')
    } finally {
      setBusyId(null)
    }
  }

  const handleDelete = async (rec) => {
    setConfirmRec(null)
    setBusyId(rec._id)
    setError('')
    try {
      await api.delete(`/api/recordings/${rec._id}`)
      if (playing?.id === rec._id) stopPlayer()
      setItems((prev) => prev.filter((i) => i._id !== rec._id))
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to delete this recording')
    } finally {
      setBusyId(null)
    }
  }

  const statusChip = (status) => {
    if (status === 'ready') return null
    const label =
      status === 'failed' ? 'Failed' : status === 'processing' ? 'Processing…' : 'Recording…'
    const color = status === 'failed' ? 'error' : 'default'
    return <Chip size="small" label={label} color={color} sx={{ height: 20, fontSize: '0.7rem' }} />
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.alt,
          color: theme.palette.text.primary,
          borderRadius: 2,
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.neutral[200]}`,
          boxShadow: 8,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{ px: 2, py: 1.5, fontWeight: 600, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center' }}
      >
        <Box flex={1} minWidth={0}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700 }}>
            Meeting recordings
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {groupName}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={load} disabled={loading}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ px: 2, py: 1.5, backgroundColor: theme.palette.background.alt }}>
        {error && (
          <Typography variant="caption" color="error" display="block" mb={1}>
            {error}
          </Typography>
        )}

        {loading && items.length === 0 ? (
          <Box textAlign="center" py={3}>
            <CircularProgress size={22} />
          </Box>
        ) : items.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No recordings yet. A host can record a large meeting with the red button.
          </Typography>
        ) : (
          <List disablePadding>
            {items.map((r) => {
              const canDelete = (iAmOwner || r.startedBy === myId) && !['starting', 'recording'].includes(r.status)
              const busy = busyId === r._id

              return (
                <ListItem
                  key={r._id}
                  disableGutters
                  sx={{ display: 'block', py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box flex={1} minWidth={0}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {new Date(r.startedAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                        </Typography>
                        {statusChip(r.status)}
                      </Box>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {r.startedByName ? `Recorded by ${r.startedByName}` : 'Recorded'}
                        {r.status === 'ready' ? ` · ${fmtDuration(r.duration)} · ${fmtSize(r.size)}` : ''}
                        {r.status === 'failed' && r.error ? ` · ${r.error}` : ''}
                      </Typography>
                    </Box>

                    {r.status === 'ready' && (
                      <>
                        <Tooltip title={playing?.id === r._id ? 'Close player' : 'Play'}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy}
                              onClick={() => handlePlay(r)}
                              sx={{ color: accent }}
                            >
                              {busy ? <CircularProgress size={18} /> : playing?.id === r._id ? <Close fontSize="small" /> : <PlayArrow />}
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Download">
                          <span>
                            <IconButton size="small" disabled={busy} onClick={() => handleDownload(r)}>
                              <Download fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}

                    {canDelete && (
                      <Tooltip title="Delete">
                        <span>
                          <IconButton size="small" disabled={busy} onClick={() => setConfirmRec(r)} sx={{ color: theme.palette.error.main }}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>

                  {playing?.id === r._id && (
                    <Box mt={1} p={0.75} sx={{ borderRadius: 1.5, backgroundColor: alpha(accent, 0.08) }}>
                      <audio src={playing.url} controls autoPlay style={{ width: '100%' }} />
                    </Box>
                  )}
                </ListItem>
              )
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={onClose} sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
          Close
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={Boolean(confirmRec)}
        onClose={() => setConfirmRec(null)}
        onConfirm={() => handleDelete(confirmRec)}
        title="Delete recording?"
        message="The recording is deleted for everybody in the group."
        item={confirmRec ? new Date(confirmRec.startedAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : ''}
        itemDetail={confirmRec?.startedByName ? `Recorded by ${confirmRec.startedByName}` : undefined}
        itemIcon={GraphicEq}
        confirmLabel="Delete recording"
      />
    </Dialog>
  )
}

export default RecordingsDialog